import { useEffect, useState, type FormEvent } from "react";
import { useRoute, Link } from "wouter";
import { collection, doc, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import {
  paths,
  INDUSTRIES,
  INDUSTRY_PRESETS,
  industryPreset,
  type Industry,
  type Tenant,
  type IntegrationLibraryEntry,
} from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, Select, cn } from "@clevis/shared-ui";
import { DepartmentsPanel, InvitesPanel, PeoplePanel, type Dept, type InviteRow as TeamInviteRow, type MemberRow as TeamMemberRow } from "../customer/TeamPage";
import { useAuth } from "../../hooks/useAuth";
import { useCardPayments } from "../../hooks/useCardPayments";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { RingLimitsCard } from "./RingAdminCards";

type MemberRow = TeamMemberRow;
type InviteRow = TeamInviteRow;
interface ConfigRow {
  slug: string;
  enabled: boolean;
}

export function AdminTenantDetail() {
  const [, params] = useRoute("/customers/:id");
  const tenantId = params?.id ?? "";
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [departments, setDepartments] = useState<Dept[]>([]);
  const [sensitive, setSensitive] = useState<Record<string, { hourlyRateCents: number | null; offShiftRadiusKm: number | null }>>({});
  const [teamTab, setTeamTab] = useState<"people" | "invites" | "departments">("people");
  const { user } = useAuth();
  const [library, setLibrary] = useState<Array<IntegrationLibraryEntry & { id: string }>>([]);
  const [configs, setConfigs] = useState<Record<string, ConfigRow>>({});
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const [impersonateReason, setImpersonateReason] = useState("");
  const { payments } = useCardPayments(tenantId || null);

  async function startImpersonation(e: FormEvent) {
    e.preventDefault();
    if (impersonateReason.trim().length < 3) return;
    setBusy(true);
    setMessage("");
    try {
      await call("startImpersonation", { tenantId, reason: impersonateReason.trim() }, { mutation: false });
      window.location.href = "/app";
    } catch (err) {
      setMessage((err as Error).message);
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(doc(db, paths.tenant(tenantId)), (s) => setTenant((s.data() as Tenant) ?? null)),
      onSnapshot(query(collection(db, paths.tenantMembers(tenantId))), (s) =>
        setMembers(
          s.docs.map((d) => ({
            uid: d.id,
            displayName: d.get("displayName") ?? "",
            email: d.get("email") ?? "",
            role: d.get("role"),
            disabled: d.get("disabled") === true,
            offShiftRadiusKm: (d.get("offShiftRadiusKm") as number | null) ?? null,
            profile: (d.get("profile") as MemberRow["profile"]) ?? null,
          })),
        ),
      ),
      onSnapshot(collection(db, paths.tenantDepartments(tenantId)), (s) =>
        setDepartments(
          s.docs
            .map((d) => {
              const data = d.data() as Partial<Omit<Dept, "id">>;
              return { ...(data as Omit<Dept, "id">), id: d.id, managerUids: data.managerUids ?? [], memberUids: data.memberUids ?? [] };
            })
            .sort((a, b) => a.name.localeCompare(b.name)),
        ),
      ),
      onSnapshot(query(collection(db, paths.integrationLibrary())), (s) =>
        setLibrary(
          s.docs
            .map((d) => ({ id: d.id, ...(d.data() as IntegrationLibraryEntry) }))
            .sort((a, b) => a.order - b.order),
        ),
      ),
      onSnapshot(query(collection(db, paths.tenantIntegrationConfigs(tenantId))), (s) => {
        const map: Record<string, ConfigRow> = {};
        for (const d of s.docs) map[d.id] = { slug: d.id, enabled: d.get("enabled") === true };
        setConfigs(map);
      }),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId]);

  async function refreshInvites() {
    try {
      const [res, team] = await Promise.all([
        call<{ invites: InviteRow[] }>("listTenantInvites", { tenantId }, { mutation: false }),
        call<{ settings: typeof sensitive }>("getTeamSettings", { tenantId }, { mutation: false }),
      ]);
      setInvites(res.invites.map((i) => ({ ...i, lastEmailedAt: i.lastEmailedAt ?? null })));
      setSensitive(team.settings);
    } catch (err) {
      setMessage((err as Error).message);
    }
  }
  useEffect(() => {
    if (tenantId) void refreshInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  async function toggleIntegration(slug: string, enabled: boolean) {
    setMessage("");
    try {
      await call("setTenantIntegration", { tenantId, slug, enabled });
    } catch (err) {
      setMessage((err as Error).message);
    }
  }

  if (!tenant) {
    return <p className="text-sm text-steel">Loading company…</p>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Link href="/" className="text-xs text-steel hover:text-ink">
            ← Customers
          </Link>
          <h1 className="text-lg font-bold">{tenant.name}</h1>
          <p className="text-xs text-steel">
            {tenant.slug} · {tenant.billingStatus}
          </p>
        </div>
        <Button variant="secondary" onClick={() => setImpersonateOpen((v) => !v)}>
          {impersonateOpen ? "Cancel" : "Log in as this company"}
        </Button>
      </div>
      {impersonateOpen ? (
        <Card className="border-brandorange/50">
          <form onSubmit={startImpersonation} className="flex items-end gap-2">
            <div className="flex-1">
              <Label>Why are you logging in? (recorded in their audit log)</Label>
              <Input
                autoFocus
                value={impersonateReason}
                onChange={(e) => setImpersonateReason(e.target.value)}
                placeholder="e.g. support ticket #142 — checking QBD queue"
              />
            </div>
            <Button type="submit" busy={busy} disabled={impersonateReason.trim().length < 3}>
              Log in
            </Button>
          </form>
        </Card>
      ) : null}
      {message ? <p className="break-all text-xs text-ink">{message}</p> : null}

      <IndustryCard key={tenantId} tenantId={tenantId} tenant={tenant} onMessage={setMessage} />
      <RingLimitsCard key={`ring-${tenantId}`} tenantId={tenantId} tenant={tenant} onMessage={setMessage} />

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-ink">Team</h2>
          <div className="inline-flex rounded-lg border border-steel/30 bg-white p-0.5 text-sm" role="tablist">
            {(
              [
                ["people", "People", members.filter((m) => !m.disabled).length],
                ["invites", "Waiting to join", invites.length],
                ["departments", "Departments", departments.length],
              ] as const
            ).map(([id, label, n]) => (
              <button
                key={id}
                role="tab"
                aria-selected={teamTab === id}
                className={cn("rounded-md px-3 py-1", teamTab === id ? "bg-ink font-semibold text-white" : "text-steel hover:text-ink")}
                onClick={() => setTeamTab(id)}
              >
                {label} <span className={cn("text-xs", teamTab === id ? "text-white/70" : "text-steel/80")}>{n}</span>
              </button>
            ))}
          </div>
        </div>
        {teamTab === "people" ? (
          <PeoplePanel
            tenantId={tenantId}
            meUid={user?.uid ?? ""}
            isAdmin
            canEditDepartments
            members={members}
            departments={departments}
            sensitive={sensitive}
            onSaved={() => void refreshInvites()}
            say={setMessage}
          />
        ) : null}
        {teamTab === "invites" ? (
          <InvitesPanel tenantId={tenantId} invites={invites} setInvites={setInvites} refresh={refreshInvites} say={setMessage} copyToken />
        ) : null}
        {teamTab === "departments" ? <DepartmentsPanel tenantId={tenantId} departments={departments} members={members} canEdit /> : null}
      </div>

      <Card>
        <CardTitle>Integrations</CardTitle>
        <p className="mb-2 text-xs text-steel">
          Enable here; the customer enters their own credentials and is connected.
        </p>
        {library.map((entry) => {
          const enabled = configs[entry.id]?.enabled === true;
          return (
            <div key={entry.id} className="flex items-center justify-between border-b border-steel/10 py-2 last:border-0">
              <div>
                <div className="text-sm text-ink">{entry.name}</div>
                <div className="text-xs text-steel">{entry.category}</div>
                {entry.id === "stripe-payments" && enabled ? (
                  <div className="text-xs text-steel">
                    {payments?.stripeAccountId
                      ? `Stripe ${payments.stripeAccountId} · ${payments.status.replace("_", " ")} · charges ${payments.chargesEnabled ? "on" : "off"} · payouts ${payments.payoutsEnabled ? "on" : "off"}${payments.livemode ? "" : " · test mode"}`
                      : "Waiting for the company to connect Stripe (Setup → Card payments)."}
                  </div>
                ) : null}
              </div>
              <Button
                variant={enabled ? "secondary" : "ghost"}
                className="px-3 py-1 text-xs"
                onClick={() => void toggleIntegration(entry.id, !enabled)}
              >
                {enabled ? "Enabled — turn off" : "Enable"}
              </Button>
            </div>
          );
        })}
        {library.length === 0 ? (
          <p className="text-sm text-steel">Library is empty — seed it from the Integrations page.</p>
        ) : null}
      </Card>

      <AuditSection tenantId={tenantId} />

      <DangerZone tenantId={tenantId} tenantName={tenant.name} />
    </div>
  );
}

// Typed-confirmation reset: wipes operational data (customers, jobs, money
// docs, workforce, routes, import state) but keeps users, settings,
// integrations and the audit log. The do-over button for a botched seed.
// Industry preset + module flags, editable after creation. Saving rewrites
// tenant.industry and featureFlags and recomputes entitlements server-side.
function IndustryCard({
  tenantId,
  tenant,
  onMessage,
}: {
  tenantId: string;
  tenant: Tenant;
  onMessage: (m: string) => void;
}) {
  const [industry, setIndustry] = useState<Industry>((tenant.industry as Industry) ?? "general");
  const preset = industryPreset(industry);
  const [flags, setFlags] = useState<Record<string, boolean>>(() => ({
    ...Object.fromEntries(industryPreset((tenant.industry as Industry) ?? "general").modules.map((m) => [m.key, m.on])),
    ...tenant.featureFlags,
  }));
  const [busy, setBusy] = useState(false);

  function pickIndustry(next: Industry) {
    setIndustry(next);
    setFlags(Object.fromEntries(industryPreset(next).modules.map((m) => [m.key, m.on])));
  }

  async function save() {
    setBusy(true);
    try {
      await call("updateTenant", { tenantId, patch: { industry, featureFlags: flags } });
      onMessage("Industry and modules saved.");
    } catch (err) {
      onMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardTitle className="mb-0">Industry & modules</CardTitle>
        <Button className="px-3 py-1 text-xs" busy={busy} onClick={() => void save()}>
          Save
        </Button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <Label>Industry</Label>
          <Select
            value={industry}
            onChange={(e) => pickIndustry(e.target.value as Industry)}
          >
            {INDUSTRIES.map((i) => (
              <option key={i} value={i}>
                {INDUSTRY_PRESETS[i].label}
              </option>
            ))}
          </Select>
          {preset.planned.length > 0 ? (
            <p className="mt-1 text-xs text-steel">Roadmap: {preset.planned.join(", ")}.</p>
          ) : null}
        </div>
        <div className="space-y-1">
          {preset.modules.map((m) => (
            <label key={m.key} className="flex cursor-pointer items-center gap-2 text-sm" title={m.hint}>
              <input
                type="checkbox"
                checked={flags[m.key] ?? m.on}
                onChange={(e) => setFlags({ ...flags, [m.key]: e.target.checked })}
              />
              <span className="text-ink">{m.label}</span>
            </label>
          ))}
        </div>
      </div>
    </Card>
  );
}

function DangerZone({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  async function reset() {
    setBusy(true);
    setNote("");
    try {
      await call("resetTenantData", { tenantId, confirm });
      setNote("Done — this company's data is back to scratch. Users, settings and integrations kept.");
      setOpen(false);
      setConfirm("");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="border-danger/40">
      <div className="flex items-center justify-between">
        <CardTitle className="mb-0 text-danger">Danger zone</CardTitle>
        <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => setOpen((v) => !v)}>
          {open ? "Close" : "Reset data…"}
        </Button>
      </div>
      {note ? <p className="mt-2 text-xs text-ink">{note}</p> : null}
      {open ? (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-steel">
            Deletes <strong>{tenantName}</strong>'s customers, jobs, estimates, invoices, requests,
            routes, time entries, shifts, payroll runs, QuickBooks queues and import history — back to
            an empty company. Keeps users, company settings, integrations (keys stay connected), billing
            and the audit log. This cannot be undone.
          </p>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label>Type CONFIRM to proceed</Label>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="CONFIRM" />
            </div>
            <Button variant="danger" busy={busy} disabled={confirm !== "CONFIRM"} onClick={() => void reset()}>
              Reset this company
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

function AuditSection({ tenantId }: { tenantId: string }) {
  const [events, setEvents] = useState<Array<{ id: string; action: string; actorUid: string; at: number }>>([]);
  useEffect(() => {
    if (!tenantId) return;
    const q = query(collection(db, paths.tenantAuditLog(tenantId)), orderBy("at", "desc"), limit(30));
    return onSnapshot(q, (snap) => {
      setEvents(
        snap.docs.map((d) => ({
          id: d.id,
          action: d.get("action"),
          actorUid: d.get("actorUid"),
          at: d.get("at"),
        })),
      );
    });
  }, [tenantId]);
  return (
    <Card className="p-0">
      <CardTitle className="px-4 pt-3">Recent activity</CardTitle>
      {events.map((e) => (
        <div key={e.id} className="flex items-center justify-between border-b border-steel/10 px-4 py-1.5 text-xs last:border-0">
          <span className="text-ink">{e.action}</span>
          <span className="text-steel">
            {new Date(e.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </span>
        </div>
      ))}
      {events.length === 0 ? <div className="px-4 py-4 text-center text-xs text-steel">No activity yet.</div> : null}
    </Card>
  );
}
